"use client";

import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../Dialog";
import { Button } from "../Button";
import { Slider } from "../Slider";
import type {
  IImageUploadProps,
  IImageCompressionDialogProps,
  ICompressedImage,
} from "./types";
import {
  DEFAULT_COMPRESSION_QUALITY,
  DEFAULT_MAX_FILE_SIZE,
  FILE_SIZE_1_MB,
  COMPRESSION_DEBOUNCE_MS,
  IMAGE_FILE_ACCEPTED,
  OUTPUT_FORMATS,
  IMAGE_UPLOAD_STYLES,
  compressBlob,
  blobToDataUrl,
  formatFileSize,
  calculateSavings,
} from "./constants";

/**
 * Image Compression Dialog - Modal with side-by-side comparison and quality adjustment
 */
function ImageCompressionDialog({
  open,
  onOpenChange,
  sourceBlob,
  fileName,
  originalSize,
  onCompressed,
  onConfirm,
  defaultQuality = DEFAULT_COMPRESSION_QUALITY,
  targetSizeThreshold = FILE_SIZE_1_MB,
  testId,
}: IImageCompressionDialogProps) {
  const [quality, setQuality] = useState(defaultQuality);
  const [originalDataUrl, setOriginalDataUrl] = useState<string>("");
  const [webpResult, setWebpResult] = useState<ICompressedImage | null>(null);
  const [jpegResult, setJpegResult] = useState<ICompressedImage | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const compressionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load original preview
  useEffect(() => {
    if (sourceBlob) {
      blobToDataUrl(sourceBlob).then(setOriginalDataUrl);
    }
  }, [sourceBlob]);

  // Compress with debounce
  useEffect(() => {
    if (!sourceBlob || !open) return;

    if (compressionTimeoutRef.current) {
      clearTimeout(compressionTimeoutRef.current);
    }

    compressionTimeoutRef.current = setTimeout(async () => {
      setIsCompressing(true);
      try {
        const [webpBlob, jpegBlob] = await Promise.all([
          compressBlob(sourceBlob, OUTPUT_FORMATS.WEBP, quality),
          compressBlob(sourceBlob, OUTPUT_FORMATS.JPEG, quality),
        ]);

        const [webpDataUrl, jpegDataUrl] = await Promise.all([
          blobToDataUrl(webpBlob),
          blobToDataUrl(jpegBlob),
        ]);

        const webpCompressed: ICompressedImage = {
          blob: webpBlob,
          mimeType: OUTPUT_FORMATS.WEBP,
          size: webpBlob.size,
          dataUrl: webpDataUrl,
        };

        const jpegCompressed: ICompressedImage = {
          blob: jpegBlob,
          mimeType: OUTPUT_FORMATS.JPEG,
          size: jpegBlob.size,
          dataUrl: jpegDataUrl,
        };

        setWebpResult(webpCompressed);
        setJpegResult(jpegCompressed);

        onCompressed?.({
          original: {
            file: new File([sourceBlob], fileName),
            size: originalSize,
            dataUrl: originalDataUrl,
          },
          webp: webpCompressed,
          jpeg: jpegCompressed,
          quality,
        });
      } catch (error) {
        console.error("Compression failed:", error);
      } finally {
        setIsCompressing(false);
      }
    }, COMPRESSION_DEBOUNCE_MS);

    return () => {
      if (compressionTimeoutRef.current) {
        clearTimeout(compressionTimeoutRef.current);
      }
    };
  }, [sourceBlob, quality, fileName, originalSize, originalDataUrl, onCompressed, open]);

  const handleConfirm = () => {
    if (webpResult && jpegResult) {
      onConfirm(webpResult.blob, jpegResult.blob);
      onOpenChange(false);
    }
  };

  const getSizeClass = (size: number) => {
    if (size <= targetSizeThreshold) return "text-green-600";
    if (size <= targetSizeThreshold * 2) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl sm:max-w-4xl overflow-y-auto"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>Compress Image</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview Grid - 3 columns */}
          <div className="grid grid-cols-3 gap-6">
            {/* Original */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 text-center">Original</div>
              <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                {originalDataUrl && (
                  <img
                    src={originalDataUrl}
                    alt="Original"
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <div className={cn("text-sm text-center font-medium", getSizeClass(originalSize))}>
                {formatFileSize(originalSize)}
              </div>
            </div>

            {/* WebP */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 text-center">
                WebP
              </div>
              <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                {isCompressing ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-500 rounded-full" />
                  </div>
                ) : webpResult ? (
                  <img
                    src={webpResult.dataUrl}
                    alt="WebP preview"
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
              <div className={cn("text-sm text-center font-medium", webpResult ? getSizeClass(webpResult.size) : "text-gray-400")}>
                {webpResult ? (
                  <>
                    {formatFileSize(webpResult.size)}
                    <span className="text-gray-400 ml-1 font-normal">
                      (-{calculateSavings(originalSize, webpResult.size)}%)
                    </span>
                  </>
                ) : (
                  "Compressing..."
                )}
              </div>
            </div>

            {/* JPEG */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 text-center">
                JPEG
              </div>
              <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                {isCompressing ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-500 rounded-full" />
                  </div>
                ) : jpegResult ? (
                  <img
                    src={jpegResult.dataUrl}
                    alt="JPEG preview"
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
              <div className={cn("text-sm text-center font-medium", jpegResult ? getSizeClass(jpegResult.size) : "text-gray-400")}>
                {jpegResult ? (
                  <>
                    {formatFileSize(jpegResult.size)}
                    <span className="text-gray-400 ml-1 font-normal">
                      (-{calculateSavings(originalSize, jpegResult.size)}%)
                    </span>
                  </>
                ) : (
                  "Compressing..."
                )}
              </div>
            </div>
          </div>

          {/* Quality Slider */}
          <div className="space-y-3 px-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">Quality</label>
              <span className="text-sm text-gray-500 font-medium">{quality}%</span>
            </div>
            <Slider
              value={[quality]}
              onValueChange={(value) => setQuality(value[0])}
              min={10}
              max={100}
              step={5}
              className="w-full"
              data-testid={testId ? `${testId}-quality-slider` : undefined}
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>Smaller file</span>
              <span>Better quality</span>
            </div>
          </div>

          {/* Info note */}
          <p className="text-xs text-gray-500 text-center">
            Both WebP and JPEG versions will be saved. WebP is used for modern browsers, JPEG as fallback.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isCompressing || !webpResult || !jpegResult}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {isCompressing ? "Compressing..." : "Use Images"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ImageUpload component with built-in compression dialog
 */
const ImageUpload = React.forwardRef<HTMLDivElement, IImageUploadProps>(
  (
    {
      onFileSelect,
      onCompressed,
      onConfirm,
      onError,
      maxFileSize = DEFAULT_MAX_FILE_SIZE,
      accept = IMAGE_FILE_ACCEPTED,
      defaultQuality = DEFAULT_COMPRESSION_QUALITY,
      showCompressionPanel = true,
      targetSizeThreshold = FILE_SIZE_1_MB,
      label,
      placeholder = "Click or drag to upload image",
      currentImageUrl,
      disabled = false,
      isUploading = false,
      className,
      testId,
    },
    ref
  ) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [sourceBlob, setSourceBlob] = useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Cleanup preview URL on unmount
    useEffect(() => {
      return () => {
        if (previewUrl && !currentImageUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    }, [previewUrl, currentImageUrl]);

    const validateFile = useCallback(
      (file: File): boolean => {
        setError(null);

        if (!file.type.startsWith("image/")) {
          const errorMsg = "Invalid file type. Please select an image.";
          setError(errorMsg);
          onError?.(errorMsg);
          return false;
        }

        if (file.size > maxFileSize) {
          const errorMsg = `File too large. Maximum size is ${formatFileSize(maxFileSize)}.`;
          setError(errorMsg);
          onError?.(errorMsg);
          return false;
        }

        return true;
      },
      [maxFileSize, onError]
    );

    const handleFile = useCallback(
      (file: File) => {
        if (!validateFile(file)) return;

        setSelectedFile(file);
        onFileSelect?.(file);

        // Create blob for compression
        const blob = new Blob([file], { type: file.type });
        setSourceBlob(blob);

        // Create preview URL
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);

        if (showCompressionPanel) {
          setDialogOpen(true);
        }
      },
      [validateFile, onFileSelect, showCompressionPanel]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset input to allow selecting same file again
      e.target.value = "";
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragActive(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFile(file);
      }
    };

    const handleClick = () => {
      if (!disabled && inputRef.current) {
        inputRef.current.click();
      }
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedFile(null);
      setSourceBlob(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setDialogOpen(false);
      setError(null);
    };

    const handleDialogConfirm = (webpBlob: Blob, jpegBlob: Blob) => {
      setDialogOpen(false);
      onConfirm?.(webpBlob, jpegBlob);
    };

    const handleDialogCancel = () => {
      setDialogOpen(false);
      // Clear the selected file since user cancelled
      setSelectedFile(null);
      setSourceBlob(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    };

    const displayUrl = previewUrl || currentImageUrl;

    return (
      <div ref={ref} className={cn(IMAGE_UPLOAD_STYLES.container, className)}>
        {label && (
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="sr-only"
          disabled={disabled || isUploading}
          data-testid={testId ? `${testId}-input` : undefined}
        />

        {displayUrl ? (
          /* Show preview */
          <div className={IMAGE_UPLOAD_STYLES.preview}>
            <img
              src={displayUrl}
              alt="Preview"
              className={IMAGE_UPLOAD_STYLES.previewImage}
            />
            {isUploading && (
              <div className={IMAGE_UPLOAD_STYLES.loadingOverlay}>
                <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-500 rounded-full" />
              </div>
            )}
            {!isUploading && !disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className={IMAGE_UPLOAD_STYLES.removeButton}
                aria-label="Remove image"
                data-testid={testId ? `${testId}-remove` : undefined}
              >
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          /* Show dropzone */
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={handleClick}
            onKeyDown={(e) => e.key === "Enter" && handleClick()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              IMAGE_UPLOAD_STYLES.dropzone,
              isDragActive && IMAGE_UPLOAD_STYLES.dropzoneActive,
              disabled && IMAGE_UPLOAD_STYLES.dropzoneDisabled
            )}
            data-testid={testId ? `${testId}-dropzone` : undefined}
          >
            <svg
              className={IMAGE_UPLOAD_STYLES.uploadIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className={IMAGE_UPLOAD_STYLES.uploadText}>{placeholder}</p>
            <p className={IMAGE_UPLOAD_STYLES.uploadHint}>
              Max {formatFileSize(maxFileSize)}
            </p>
          </div>
        )}

        {error && (
          <p className={IMAGE_UPLOAD_STYLES.errorText} data-testid={testId ? `${testId}-error` : undefined}>
            {error}
          </p>
        )}

        {/* Compression Dialog */}
        {sourceBlob && selectedFile && (
          <ImageCompressionDialog
            open={dialogOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleDialogCancel();
              }
            }}
            sourceBlob={sourceBlob}
            fileName={selectedFile.name}
            originalSize={selectedFile.size}
            onCompressed={onCompressed}
            onConfirm={handleDialogConfirm}
            defaultQuality={defaultQuality}
            targetSizeThreshold={targetSizeThreshold}
            testId={testId ? `${testId}-compression` : undefined}
          />
        )}
      </div>
    );
  }
);

ImageUpload.displayName = "ImageUpload";

export { ImageUpload, ImageCompressionDialog };
